/* eslint-disable max-lines */
import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from '@wix/wix-i18n-config';
import {
  Box,
  Dropdown,
  DropdownLayoutValueOption,
  Input,
  Pagination,
  Tabs,
  Text,
} from '@wix/design-system';
import css from './index.module.scss';
import { ArticleResolution, ArticleSortType, TsUtils } from '@wix/answers-api';
import { useWindowSize } from '../../hooks/useWindowSize';
import {
  ALL_RESOLUTIONS,
  ArticleSearchResult,
  ArticleSearchResultItem,
  RoadmapFeatureResolution,
  RoadmapFilterType,
} from '../../types';
import RoadmapArticleListItem from './RoadmapArticleListItem';
import { Context } from '../../context';
import { PAGE_SIZE } from '../../pages/roadmap';
import { DATA_HOOKS } from '../../dataHooks';
import { isMobile } from '../../utils/userAgent';
import { RoadmapEmptyState } from './RoadmapEmptyState';
import { RenderHTML } from '../RenderHTML';

export type RoadmapArticleListProps = {
  articleSearchResult: ArticleSearchResult;
  selectedLabel: {
    id: string;
    name: string;
  };
  resolution: RoadmapFeatureResolution;
  onChangeResolution: (resolution: RoadmapFeatureResolution) => void;
  currentPage: number;
  onChangePage: (pageNumber: number) => void;
  followArticle: (articleId: string) => void;
  unfollowArticle: (articleId: string) => void;
  onSelectArticle: (article: ArticleSearchResultItem) => void;
  onSelectLabel: (label: { id: string; name: string }) => void;
  locale: string;
  sortType: ArticleSortType;
  onSortChange: (sortType: ArticleSortType) => void;
  filterType: RoadmapFilterType;
  onFilterChange: (filterType: RoadmapFilterType) => void;
  userFollowedArticle: string[];
  labels: { id: string; name: string }[];
  isLoggedInUser: boolean;
};
export const ALL_SORT_OPTION = [
  ArticleSortType.LAST_PUBLISH_DATE_DESC,
  ArticleSortType.VOTES_DESC,
];

export const allFilterOptions = TsUtils.enumToArray(
  RoadmapFilterType
) as RoadmapFilterType[];

export const RoadmapArticleList: FunctionComponent<RoadmapArticleListProps> = ({
  articleSearchResult,
  selectedLabel,
  resolution,
  onChangeResolution,
  followArticle,
  unfollowArticle,
  labels,
  onSelectArticle,
  currentPage,
  onChangePage,
  locale,
  onSelectLabel,
  sortType,
  onSortChange,
  filterType,
  onFilterChange,
  isLoggedInUser,
  userFollowedArticle,
}) => {
  const [articleList, setArticleList] = useState(articleSearchResult.items);
  const [totalLength, setTotalLength] = useState(
    articleSearchResult.itemsCount
  );
  useWindowSize();
  const { t } = useTranslation();
  const resolutions = ['all', ...ALL_RESOLUTIONS];
  const context = useContext(Context);
  const isMobileView = isMobile(context);

  useEffect(() => {
    setArticleList(articleSearchResult.items);
    setTotalLength(articleSearchResult.itemsCount);
  }, [articleSearchResult]);

  const onChangePageNumber = (event: {
    event: React.SyntheticEvent<Element, Event>;
    page: number;
  }): void => {
    onChangePage(event.page);
  };

  const getRoadmapLabelName = (labelId: string) => {
    const roadmapLabel = labels.find((l) => l.id === labelId);
    return roadmapLabel ? roadmapLabel.name : null;
  };

  const renderArticleItem = (article: ArticleSearchResultItem) => {
    const articleLabelsNames = article.labels
      ? article.labels
          .filter(
            (articleLabel) => getRoadmapLabelName(articleLabel.id) !== null
          )
          .map((roadmapArticleLabel) => ({
            name: getRoadmapLabelName(roadmapArticleLabel.id) || '',
            id: roadmapArticleLabel.id,
          }))
      : [];
    return (
      <div key={article.id} className={css.articleListItemWrapper}>
        <RoadmapArticleListItem
          key={article.id}
          article={article}
          articleLabels={articleLabelsNames}
          onSelectLabel={onSelectLabel}
          followArticle={followArticle}
          unhallowArticle={unfollowArticle}
          onSelectArticle={onSelectArticle}
          locale={locale}
          sortType={sortType}
          filter={filterType}
          userFollowedArticle={userFollowedArticle}
          onSelectStatus={onChangeResolution}
        />
        <div className={css.divider} />
      </div>
    );
  };

  const sortOptions: DropdownLayoutValueOption[] = ALL_SORT_OPTION.map(
    (sortOption) => ({
      id: sortOption,
      value: t(`roadmap.sort-option.${sortOption}`),
    })
  );

  const onChangeSortType = (
    option: DropdownLayoutValueOption,
    sameOptionWasPicked: boolean
  ) => {
    if (!sameOptionWasPicked) {
      onSortChange(option.id as ArticleSortType);
    }
  };
  const sortPrefix = (
    <Input.Affix>
      <Text className={css.sortMenuPrefix} weight="thin" size="medium">
        {t(`roadmap.sort-option.selected`)}
      </Text>
    </Input.Affix>
  );
  const sortMenu = (
    <Dropdown
      selectedId={sortType}
      prefix={sortPrefix}
      dataHook={DATA_HOOKS.ROADMAP_SORT_MENU}
      onSelect={onChangeSortType}
      className={css.roadmapSortMenu}
      border="round"
      placeholder={t('roadmap.sort-menu.placeholder')}
      options={sortOptions}
      popoverProps={{ maxWidth: '253px' }}
    />
  );

  const FilterOptions: DropdownLayoutValueOption[] = allFilterOptions.map(
    (filterOption) => ({
      id: filterOption,
      value: t(`roadmap.filter-option.${filterOption}`),
    })
  );

  const onChangeFilterType = (
    option: DropdownLayoutValueOption,
    sameOptionWasPicked: boolean
  ) => {
    if (!sameOptionWasPicked) {
      onFilterChange(option.id as RoadmapFilterType);
    }
  };

  const filterPrefix = (
    <Input.Affix>
      <Text className={css.filterMenuPrefix} weight="thin" size="medium">
        {t(`roadmap.filter-option.prefix`)}
      </Text>
    </Input.Affix>
  );
  const filterMenu = (
    <Dropdown
      prefix={filterPrefix}
      selectedId={filterType}
      dataHook={DATA_HOOKS.ROADMAP_FILTER_MENU}
      onSelect={onChangeFilterType}
      className={css.roadmapFilterMenu}
      border="round"
      placeholder={''}
      options={FilterOptions}
      textOverflow="ellipsis"
      popoverProps={{ maxWidth: '253px' }}
    />
  );

  const numPages = Math.ceil(totalLength / PAGE_SIZE);
  const selectedLabelHeader = (
    <Text className={css.selectedLabelTitle}>{selectedLabel.name}</Text>
  );

  return (
    <Box
      key="article-list"
      direction="vertical"
      align="center"
      width={isMobileView ? undefined : '100%'}
      className={`${css.articleList}`}
    >
      <Box className={css.articleListInner} direction="vertical">
        <Box
          className={css.headerWrapper}
          align="space-between"
          direction="horizontal"
          verticalAlign={!isMobileView ? 'bottom' : undefined}
        >
          {selectedLabelHeader}
          <Box direction="horizontal" align="right" className={css.actionMenu}>
            {sortMenu}
            {filterMenu}
          </Box>
        </Box>
        {!isMobileView && (
          <Tabs
            className={`${css.articleResolutionTabs}`}
            activeId={resolution}
            onClick={(value) =>
              onChangeResolution(value.id as ArticleResolution)
            }
            dataHook={DATA_HOOKS.ARTICLE_RESOLUTION_TABS}
            items={resolutions.map((res) => ({
              dataHook:
                res === resolution
                  ? DATA_HOOKS.ARTICLE_RESOLUTION_TAB_ACTIVE
                  : DATA_HOOKS.ARTICLE_RESOLUTION_TAB,
              id: res,
              title: t(`roadmap.article-list.resolutions.${res}`),
            }))}
          />
        )}
        {
          <Box direction="vertical" className={css.articleListWrapper}>
            {articleList.length ? (
              articleList.map((article) => renderArticleItem(article))
            ) : (
              <RoadmapEmptyState
                filterType={filterType}
                isLoggedInUser={isLoggedInUser}
              />
            )}
          </Box>
        }
        {totalLength > PAGE_SIZE ? (
          <Box
            paddingTop="30px"
            align="center"
            direction="horizontal"
            width={'100%'}
          >
            <Pagination
              className={css.roadmapPagination}
              onChange={onChangePageNumber}
              currentPage={currentPage}
              totalPages={numPages}
            />
          </Box>
        ) : null}
        {articleList.length ? (
          <Box width={'100%'}>
            <RenderHTML
              textElement={{
                size: 'tiny',
                weight: 'thin',
                className: `${css.noteMark} ${
                  articleList.length === 0 ? css.withEmptyState : ''
                }`,
              }}
              className={css.NumberOfResultsMobile}
              html={t('roadmap.article-list.note-mark')}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};
